import React, { useEffect, useRef } from 'react';
import '../styles/Root.css';
import '../styles/Header.css';
import '../styles/Footer.css';
import '../styles/LeChetom.css';
import Archives from './Archives';

// Import des images
import heroImage from '../assets/chetom/lechetom.jpg';
import iconographie1 from '../assets/chetom/Iconographie/Vignette - XVIIIe - Officier et dame de qualité par Geoges Duplessis 1867.jpg';
import cahierChants from '../assets/chetom/Documents/Cahier de chants - page 62.jpeg';
import documentMarchand from '../assets/chetom/Documents/Marchand - MTDM.JPG';
import missionMonteil from '../assets/chetom/Cartographie/Vignette cartographie - Mission Monteil.jpg';
import dicMarseilleSaigon from '../assets/chetom/Bibliothèque/9 DIC de Marseille à Saigon.jpg';
import aob423 from '../assets/chetom/Bibliothèque/AOB 423.jpg';
import aob434 from '../assets/chetom/Bibliothèque/AOB 434.jpg';
import revueTroupesColoniales from '../assets/chetom/Bibliothèque/Revue des troupes coloniales n° 280.png';
import tropiques341 from '../assets/chetom/Bibliothèque/Tropiques n°341.jpg';
import tropiques365 from '../assets/chetom/Bibliothèque/Tropiques n°365.jpg';
import souvenirsArtilleur from '../assets/chetom/Iconographie/souvenirsartilleur.JPG';
import priseTananarive from '../assets/chetom/Iconographie/Imprimerie de Wissembourg 1897 - Prise de Tananarive en 1895.jpg';
import afficheIndochine from '../assets/chetom/Iconographie/delivrerindochine.jpg';
import troisCouleurs from '../assets/chetom/Iconographie/Trois couleurs.jpg';
import jonqueSogno from '../assets/chetom/Iconographie/Jonque - SOGNO.jpg';
import societeRepublicaineToulon from '../assets/chetom/societerepublicainetoulon.png';
import dicAofRhin from '../assets/chetom/Bibliothèque/9dicrhin.jpg';
import derniereCartouche from '../assets/dernierecartouche.jpg';

// Import de la vidéo
import archivesVideo from '../assets/videosmusee/archives.mp4';

// Import des PDF
import pdfInventaire1516 from '../assets/chetom/inventaire1516h.pdf';
import pdfInventaire1718 from '../assets/chetom/inventaire1718h.pdf';
import pdfRecherches from '../assets/chetom/documentsderecherche.pdf';

const LeChetom = () => {
    const galleryRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('.timeline-item, .stat-circle, .research-card, .fade-in').forEach((el) => {
            observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    const scrollGallery = (direction) => {
        const gallery = galleryRef.current;
        if (gallery) {
            const itemWidth = gallery.firstElementChild?.offsetWidth + 20 || 320;
            gallery.scrollBy({ left: direction * itemWidth, behavior: 'smooth' });
        }
    };

    return (
        <main>
            {/* ─── HERO ─────────────────────────────────────────────── */}
            <section className="hero-section">
                <div className="hero-overlay" aria-hidden="true" />
                <div className="container hero-main-layout">
                    {/* Image à gauche en Desktop */}
                    <div className="hero-image-container fade-in">
                        <div className="hero-image-frame">
                            <img src={heroImage} alt="Le CHETOM — vue extérieure" className="hero-image" />
                            <div className="hero-image-caption">Musée des Troupes de marine — Fréjus</div>
                        </div>
                    </div>

                    {/* Bloc Texte */}
                    <div className="hero-text-container">
                        <div className="hero-badge fade-in">
                            <span>Fondé en 1996</span>
                        </div>
                        <div className="hero-content">
                            <div className="hero-eyebrow">Centre d'Histoire et d'Études des</div>
                            <h1>Troupes d'Outre-Mer</h1>
                            <div className="hero-rule" aria-hidden="true" />
                            <p>
                                Le Centre d'histoire et d'études des Troupes d'Outre-Mer naît en 1996 au sein du musée des Troupes de marine à Fréjus. Rattaché à la direction du musée, il a pour mission de conserver, valoriser et diffuser le patrimoine historique des troupes coloniales et des troupes de marine françaises.
                            </p>
                            <p className="hero-highlight">
                                Ce centre est d'abord une structure de recherche dont l'objectif est de développer des travaux de
                                grande qualité sur l'histoire militaire des troupes coloniales et des troupes de marine.
                            </p>
                            <a className="btn btn-hero" href="/pages/FormulaireChetom">
                                <span>Contacter le CHETOM</span>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── STATS ────────────────────────────────────────────── */}
            <section className="archive-stats" aria-label="Chiffres clés">
                <div className="stat-circle">
                    <span className="stat-number">13 680</span>
                    <span className="stat-label">Documents référencés</span>
                </div>
                <div className="stat-divider" aria-hidden="true" />
                <div className="stat-circle">
                    <span className="stat-number">850</span>
                    <span className="stat-label">Cartons d'archives</span>
                </div>
                <div className="stat-divider" aria-hidden="true" />
                <div className="stat-circle">
                    <span className="stat-number">500+</span>
                    <span className="stat-label">Fonds privés</span>
                </div>
            </section>

            {/* ─── ESPACE RECHERCHE ─────────────────────────────────── */}
            <section className="research-space" id="recherche">
                <div className="container">
                    <div className="section-header fade-in">
                        <span className="section-tag">Archives & Recherche</span>
                        <h2>Accès aux fonds documentaires</h2>
                    </div>

                    <div className="research-grid">
                        <div className="research-info fade-in">
                            <div className="research-block">
                                <div className="research-block-number">01</div>
                                <div className="research-block-body">
                                    <h3>Présentation des fonds</h3>
                                    <p>Le Centre d'Histoire et d'Étude des Troupes de Marine (CHETOM) met à disposition des chercheurs plusieurs fonds d'archives organisés comme suit :</p>
                                    <ul className="styled-list">
                                        <li><strong>Fonds publics :</strong> Série 15H, Série 16H</li>
                                        <li><strong>Fonds privés :</strong> Série 17H, Série 18H</li>
                                    </ul>
                                    <p>Ces inventaires permettent d'identifier précisément les documents conservés au sein du centre.</p>
                                </div>
                            </div>

                            <div className="research-block">
                                <div className="research-block-number">02</div>
                                <div className="research-block-body">
                                    <h3>Modalités de recherche</h3>
                                    <p>Les chercheurs sont invités à consulter les inventaires afin de cibler les documents utiles à leurs travaux.</p>
                                    <p>Dans un second temps, toute demande doit être formulée par écrit en précisant :</p>
                                    <ul className="styled-list">
                                        <li>Le sujet de la recherche</li>
                                        <li>La cote du carton</li>
                                        <li>Le numéro du dossier souhaité</li>
                                    </ul>
                                    <p className="important-note">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                                        Toute demande imprécise ne pourra être traitée efficacement.
                                    </p>
                                </div>
                            </div>

                            <div className="research-block">
                                <div className="research-block-number">03</div>
                                <div className="research-block-body">
                                    <h3>Modalités de consultation</h3>
                                    <p>La consultation des archives s'effectue uniquement sur rendez-vous. Après réception de la demande, un créneau sera proposé en fonction des disponibilités et de la communicabilité des documents.</p>
                                    <div className="opening-hours">
                                        <div className="opening-hours-title">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                            Horaires d'ouverture
                                        </div>
                                        <ul>
                                            <li>Du lundi au jeudi</li>
                                            <li>09h00 – 12h00 &amp; 13h30 – 17h00</li>
                                        </ul>
                                    </div>
                                    <p className="disclaimer">
                                        <strong>À noter :</strong> La communication de certains documents peut être soumise à des restrictions conformément à la réglementation en vigueur (protection des personnes, secret de la défense nationale, etc.).
                                    </p>
                                </div>
                            </div>
                        </div>

                        <aside className="research-downloads">
                            <div className="downloads-header">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                <h3>Inventaires à télécharger</h3>
                            </div>
                            <div className="download-cards">
                                <div className="research-card">
                                    <div className="card-icon" aria-hidden="true">📄</div>
                                    <h4>Inventaire 15H – 16H</h4>
                                    <p>Fonds publics</p>
                                    <a href={pdfInventaire1516} download className="btn btn-download">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                        Télécharger le PDF
                                    </a>
                                </div>
                                <div className="research-card">
                                    <div className="card-icon" aria-hidden="true">📄</div>
                                    <h4>Inventaire 17H – 18H</h4>
                                    <p>Fonds privés</p>
                                    <a href={pdfInventaire1718} download className="btn btn-download">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                        Télécharger le PDF
                                    </a>
                                </div>
                                <div className="research-card">
                                    <div className="card-icon" aria-hidden="true">📄</div>
                                    <h4>Documents de recherche</h4>
                                    <p>Guide complet</p>
                                    <a href={pdfRecherches} download className="btn btn-download">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                        Télécharger le PDF
                                    </a>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            {/* ─── VIDÉO ────────────────────────────────────────────── */}
            <section className="video-section fade-in">
                <div className="container">
                    <div className="video-wrapper">
                        <div className="video-label">
                            <span className="video-dot" aria-hidden="true" />
                            Présentation
                        </div>
                        <video className="video-player" controls>
                            <source src={archivesVideo} type="video/mp4" />
                            Votre navigateur ne supporte pas la lecture vidéo.
                        </video>
                        <p className="video-caption">Mme Crépet, Responsable du CHETOM, présente les archives</p>
                    </div>
                </div>
            </section>

            {/* ─── GALERIE ──────────────────────────────────────────── */}
            <section className="gallery-section">
                <div className="gallery-header container">
                    <div>
                        <span className="section-tag">Patrimoine visuel</span>
                        <h2>Nos Archives en Images</h2>
                    </div>
                    <div className="arrow-container" role="group" aria-label="Navigation galerie">
                        <button className="arrow left" onClick={() => scrollGallery(-1)} aria-label="Image précédente">&#10094;</button>
                        <button className="arrow right" onClick={() => scrollGallery(1)} aria-label="Image suivante">&#10095;</button>
                    </div>
                </div>
                <div className="gallery-grid" id="gallery-grid" ref={galleryRef}>
                    <div className="gallery-item">
                        <img src={iconographie1} alt="Officier et dame de qualité par Georges Duplessis, 1867" />
                        <div className="gallery-overlay">Iconographie — XVIIIe s.</div>
                    </div>
                    <div className="gallery-item">
                        <img src={cahierChants} alt="Cahier de chants — page 62" />
                        <div className="gallery-overlay">Cahier de chants</div>
                    </div>
                    <div className="gallery-item">
                        <img src={documentMarchand} alt="Document Marchand — MTDM" />
                        <div className="gallery-overlay">Document Marchand</div>
                    </div>
                    <div className="gallery-item">
                        <img src={missionMonteil} alt="Mission Monteil — cartographie" />
                        <div className="gallery-overlay">Mission Monteil</div>
                    </div>
                    <div className="gallery-item">
                        <img src={dicMarseilleSaigon} alt="9e DIC de Marseille à Saïgon" />
                        <div className="gallery-overlay">DIC Marseille – Saïgon</div>
                    </div>
                </div>
            </section>

            {/* ─── TIMELINE ─────────────────────────────────────────── */}
            <section className="timeline-vertical" aria-label="Collections">
                <div className="timeline-track" aria-hidden="true" />
                <div className="timeline-item">
                    <div className="timeline-dot" aria-hidden="true" />
                    <div className="timeline-content">
                        <span className="timeline-tag">Documents</span>
                        <h3>Archives manuscrites &amp; imprimées</h3>
                        <p>Un fonds spécialisé de 850 cartons d'archives consultables sur place, couvrant plus d'un siècle d'histoire militaire coloniale et de marine.</p>
                        <img src={societeRepublicaineToulon} alt="Société républicaine de Toulon — archives" className="timeline-img" />
                    </div>
                </div>

                <div className="timeline-item">
                    <div className="timeline-dot" aria-hidden="true" />
                    <div className="timeline-content">
                        <span className="timeline-tag">Bibliothèque</span>
                        <h3>Bibliothèque historique</h3>
                        <p>Plus de 10 000 ouvrages de référence, revues et périodiques militaires (Revue des troupes coloniales, Tropiques, etc.) sont à votre disposition.</p>
                        <div className="timeline-img-grid">
                            <img src={revueTroupesColoniales} alt="Revue des troupes coloniales" className="timeline-img-small" />
                            <img src={tropiques341} alt="Revue Tropiques" className="timeline-img-small" />
                        </div>
                    </div>
                </div>

                <div className="timeline-item">
                    <div className="timeline-dot" aria-hidden="true" />
                    <div className="timeline-content">
                        <span className="timeline-tag">Iconographie</span>
                        <h3>Fonds iconographique</h3>
                        <p>Une collection exceptionnelle de photographies, gravures et affiches retraçant le quotidien et les faits d'armes des Troupes de Marine.</p>
                        <img src={afficheIndochine} alt="Affiche Indochine" className="timeline-img" />
                    </div>
                </div>
            </section>

            <Archives />

            {/* ─── ÉQUIPE ───────────────────────────────────────────── */}
            <section className="contact-section">
                <div className="container">
                    <div className="section-header fade-in" style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                        <span className="section-tag">L'équipe</span>
                        <h2>Personnel permanent</h2>
                    </div>
                    <div className="team-members">
                        <div className="team-member fade-in">
                            <div className="team-member-img-wrap">
                                <img src={derniereCartouche} alt="Sandrine CREPET" />
                            </div>
                            <div className="member-info">
                                <h3>Sandrine CREPET</h3>
                                <p>Responsable du CHETOM</p>
                            </div>
                        </div>
                        <div className="team-member fade-in">
                            <div className="team-member-img-wrap">
                                <img src={derniereCartouche} alt="Pierre MANIER" />
                            </div>
                            <div className="member-info">
                                <h3>Pierre MANIER</h3>
                                <p>Personnel permanent</p>
                            </div>
                        </div>
                    </div>
                    <div className="contact-cta">
                        <a className="btn btn-hero" href="/pages/FormulaireChetom">
                            <span>Contacter le CHETOM</span>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        </a>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default LeChetom;